import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { useOrders } from '../context/OrdersContext';
import { useLoyalty } from '../context/LoyaltyContext';
import { PlatformLogo } from '../components/PlatformLogo';
import LoyaltySection from '../components/LoyaltySection';
import { platforms } from '../mock/mock';
import { LogOut, Package, TrendingUp, Zap, Clock, CheckCircle2, ChevronRight, Sparkles, Copy, Gift, Crown, RefreshCw } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const StatusBadge = ({ state }) => {
  const map = {
    pending: { bg: 'bg-yellow-500/10', color: 'text-yellow-400', icon: Clock },
    processing: { bg: 'bg-[#ff8247]/10', color: 'text-[#ff8247]', icon: Zap },
    delivered: { bg: 'bg-[#00b67a]/10', color: 'text-[#00b67a]', icon: CheckCircle2 }
  };
  const s = map[state.status] || map.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.color}`}>
      <Icon className="w-3 h-3" /> {state.label}
    </span>
  );
};

const OrderCard = ({ order }) => {
  const firstItem = order.items[0];
  const platform = platforms.find(p => p.name === firstItem?.platform);
  const dt = new Date(order.createdAt);
  const dateStr = dt.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="glass-card rounded-2xl p-5 lg:p-6">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${platform?.color || '#ff8247'}18` }}>
            <PlatformLogo platform={platform?.id} className="w-6 h-6" color={platform?.id === 'instagram' ? undefined : (platform?.color || '#ff8247')} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-white font-bold text-sm">Commande {order.id}</span>
              <button onClick={() => { navigator.clipboard?.writeText(order.id); toast({ title: 'Copié', description: order.id }); }}
                className="text-white/40 hover:text-white transition"><Copy className="w-3 h-3" /></button>
            </div>
            <div className="text-white/50 text-xs">{dateStr} · {order.items.length} article(s)</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge state={order.state} />
          <div className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{order.total.toFixed(2)} €</div>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        {order.items.map((it, i) => {
          const plat = platforms.find(p => p.name === it.platform);
          return (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${plat?.color || '#ff8247'}18` }}>
                <PlatformLogo platform={plat?.id} className="w-5 h-5" color={plat?.id === 'instagram' ? undefined : (plat?.color || '#ff8247')} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-semibold truncate">{it.name}</div>
                <div className="text-white/40 text-xs">{it.qty.toLocaleString('fr-FR')} × {it.quantity} · {it.platform}</div>
              </div>
              <div className="text-white/70 text-xs font-semibold">{(it.price * it.quantity).toFixed(2)} €</div>
            </div>
          );
        })}
      </div>

      <div className="mb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-white/60 text-xs font-semibold">Progression de livraison</div>
          <div className="text-white text-xs font-bold">{order.state.progress}%</div>
        </div>
        <div className="h-2 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#ff8247] via-[#ff4d1a] to-[#ff2d6f] transition-all duration-1000" style={{ width: `${order.state.progress}%` }} />
        </div>
      </div>

      <div className="flex items-center justify-between text-xs">
        <div className="text-white/50">Compte cible : <span className="text-white font-semibold">{order.handle || '—'}</span></div>
        {order.state.status !== 'delivered' && (
          <div className="flex items-center gap-1.5 text-[#ff8247]">
            <span className="pulse-dot" />
            <span className="font-semibold">Mise à jour temps réel</span>
          </div>
        )}
      </div>
    </div>
  );
};

const Account = () => {
  const { user, signout } = useAuth();
  const { userOrders } = useOrders();
  const { balance, tierInfo } = useLoyalty();
  const navigate = useNavigate();

  const [liveOrders, setLiveOrders] = useState([]);
  const [loyaltyBackend, setLoyaltyBackend] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchLive = async () => {
    if (!user?.email) return;
    setRefreshing(true);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/my/orders?email=${encodeURIComponent(user.email)}`);
      if (res.ok) {
        const d = await res.json();
        setLiveOrders(d.orders || []);
        setLoyaltyBackend(d.loyalty || null);
      }
    } catch {}
    setRefreshing(false);
  };

  useEffect(() => { fetchLive(); }, [user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => ({
    total: userOrders.length + liveOrders.length,
    pending: userOrders.filter(o => o.state.status !== 'delivered').length + liveOrders.filter(o => !['Completed','Complete','Partial','Canceled','Refunded'].includes(o.status)).length,
    spent: userOrders.reduce((s, o) => s + o.total, 0) + liveOrders.reduce((s, o) => s + (Number(o.amount_eur) || 0), 0)
  }), [userOrders, liveOrders]);

  if (!user) {
    return (
      <div>
        <AnnouncementBar />
        <Header />
        <div className="max-w-3xl mx-auto px-5 lg:px-8 py-24 text-center">
          <div className="glass-card rounded-3xl p-12">
            <div className="inline-flex w-16 h-16 rounded-2xl bg-[#ff8247]/10 items-center justify-center mb-5">
              <Sparkles className="w-8 h-8 text-[#ff8247]" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Connectez-vous à votre espace client</h1>
            <p className="text-white/60 mb-8 max-w-md mx-auto">Suivi de livraison, historique des commandes et handles enregistrés : tout est ici.</p>
            <div className="flex items-center justify-center gap-3">
              <Link to="/auth/login" className="btn-primary text-sm">Se connecter</Link>
              <Link to="/auth/register" className="btn-ghost text-sm">Créer un compte</Link>
            </div>
          </div>
        </div>
        <Footer showCTA={false} />
      </div>
    );
  }

  return (
    <div>
      <AnnouncementBar />
      <Header />

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
        <div className="grid lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-3">
            <div className="glass-card rounded-3xl p-6 sticky top-24">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#ff8247] to-[#ff2d6f] flex items-center justify-center font-black text-[#0a0a0f] text-lg">
                  {(user.name || user.email).charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-white font-semibold text-sm truncate">{user.name}</div>
                  <div className="text-white/50 text-xs truncate">{user.email}</div>
                </div>
              </div>

              <div className="space-y-2 mb-5">
                {[
                  { i: Package, l: 'Commandes', v: stats.total },
                  { i: TrendingUp, l: 'En cours', v: stats.pending },
                  { i: Sparkles, l: 'Dépensé', v: `${stats.spent.toFixed(2)} €` },
                  { i: Gift, l: 'Points fidélité', v: `${balance.toLocaleString('fr-FR')}` }
                ].map((s, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/5">
                    <div className="flex items-center gap-2 text-white/60 text-xs"><s.i className="w-4 h-4 text-[#ff8247]" /> {s.l}</div>
                    <div className="text-white font-bold text-sm">{s.v}</div>
                  </div>
                ))}
              </div>

              <div className="mb-5 p-3 rounded-xl border" style={{ background: `${tierInfo.current.color}12`, borderColor: `${tierInfo.current.color}30` }}>
                <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: tierInfo.current.color }}>Statut fidélité</div>
                <div className="text-white font-bold text-sm mt-0.5">{tierInfo.current.name}</div>
              </div>

              <button onClick={() => { signout(); navigate('/'); toast({ title: 'Déconnecté', description: 'A bientôt !' }); }}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full bg-white/5 hover:bg-red-500/10 text-white/70 hover:text-red-400 text-sm transition">
                <LogOut className="w-4 h-4" /> Se déconnecter
              </button>
            </div>
          </aside>

          <main className="lg:col-span-9">
            <div className="mb-8">
              <div className="text-[#ff8247] text-xs font-bold uppercase tracking-widest mb-3">Espace client</div>
              <h1 className="text-4xl lg:text-6xl font-bold text-white leading-none">Salut {user.name} <span className="gradient-text">👋</span></h1>
              <p className="text-white/60 mt-3">Suivez vos livraisons en temps réel ci-dessous.</p>
            </div>

            {/* Fidélité — progression vers -10% */}
            {loyaltyBackend && (
              <div data-testid="account-loyalty-card" className={`glass-card rounded-2xl p-5 mb-5 border ${loyaltyBackend.eligible ? 'border-[#00b67a]/30 bg-[#00b67a]/[0.04]' : 'border-white/10'}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${loyaltyBackend.eligible ? 'bg-[#00b67a]/15' : 'bg-[#ff8247]/10'}`}>
                    <Crown className={`w-5 h-5 ${loyaltyBackend.eligible ? 'text-[#00b67a]' : 'text-[#ff8247]'}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`text-[10px] font-bold uppercase tracking-widest ${loyaltyBackend.eligible ? 'text-[#00b67a]' : 'text-[#ff8247]'}`}>{loyaltyBackend.tier}</div>
                    <div className="text-white font-bold text-base leading-tight">
                      {loyaltyBackend.eligible
                        ? `Fidélité VIP · -${loyaltyBackend.discount_percent}% à vie ✨`
                        : `Encore ${loyaltyBackend.amount_to_unlock_eur.toFixed(2)} € pour débloquer -10% à vie`}
                    </div>
                  </div>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className={`h-full ${loyaltyBackend.eligible ? 'bg-[#00b67a]' : 'bg-gradient-to-r from-[#ff8247] to-[#ff2d6f]'}`} style={{ width: `${Math.min(100, (loyaltyBackend.cumulative_eur / loyaltyBackend.threshold_eur) * 100)}%` }} />
                </div>
                <div className="text-white/50 text-xs mt-2">{loyaltyBackend.cumulative_eur.toFixed(2)} € cumulés / {loyaltyBackend.threshold_eur.toFixed(2)} €</div>
              </div>
            )}

            {/* Suivi live des commandes réelles (Stripe → SMMHype) */}
            {liveOrders.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-white font-bold text-sm">Suivi de vos commandes</div>
                  <button onClick={fetchLive} disabled={refreshing} data-testid="account-refresh-btn"
                    className="text-white/60 hover:text-white text-xs inline-flex items-center gap-1.5 transition disabled:opacity-50">
                    <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Actualiser
                  </button>
                </div>
                <div className="space-y-3">
                  {liveOrders.map(o => {
                    const done = ['Completed','Complete','Partial'].includes(o.status);
                    const canceled = ['Canceled','Refunded'].includes(o.status);
                    const progress = (o.remains && o.qty) ? Math.max(0, Math.min(100, ((o.qty - Number(o.remains)) / o.qty) * 100)) : (done ? 100 : (o.smm_order_id ? 25 : 5));
                    return (
                      <div key={o.session_id} data-testid={`live-order-${o.smm_order_id || o.session_id}`} className="glass-card rounded-2xl p-4">
                        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                          <div className="min-w-0">
                            <div className="text-white font-semibold text-sm truncate">{o.product_name || 'Boost'}</div>
                            <div className="text-white/50 text-xs mt-0.5">{Number(o.qty || 0).toLocaleString('fr-FR')} unités · {new Date(o.created_at).toLocaleString('fr-FR', {day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}</div>
                          </div>
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${canceled ? 'bg-red-500/10 text-red-400' : done ? 'bg-[#00b67a]/10 text-[#00b67a]' : 'bg-[#ff8247]/10 text-[#ff8247]'}`}>
                            {canceled ? <Clock className="w-3 h-3" /> : done ? <CheckCircle2 className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
                            {o.status || 'Pending'}
                          </div>
                        </div>
                        <div className="text-white/50 text-[11px] truncate mb-2">🔗 {o.handle}</div>
                        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div className={`h-full ${done ? 'bg-[#00b67a]' : canceled ? 'bg-red-500/40' : 'bg-gradient-to-r from-[#ff8247] to-[#ff2d6f]'}`} style={{ width: `${progress}%` }} />
                        </div>
                        <div className="flex items-center justify-between mt-2 text-[11px]">
                          <span className="text-white/50">{o.smm_order_id ? `#${o.smm_order_id}` : 'En attente de traitement'}</span>
                          <span className="text-white font-semibold">{Number(o.amount_eur || 0).toFixed(2)} €</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {userOrders.length === 0 && liveOrders.length === 0 ? (
              <div className="glass-card rounded-3xl p-16 text-center">
                <Package className="w-12 h-12 text-[#ff8247] mx-auto mb-4" />
                <div className="text-white text-xl font-semibold mb-2">Aucune commande pour l’instant</div>
                <div className="text-white/50 mb-8 max-w-md mx-auto">Découvrez nos packs et lancez votre premier boost en 2 clics.</div>
                <Link to="/collections" className="btn-primary inline-flex items-center gap-2 text-sm">Découvrir les packs <ChevronRight className="w-4 h-4" /></Link>
              </div>
            ) : (
              <div className="space-y-4">
                {userOrders.map(order => <OrderCard key={order.id} order={order} />)}
              </div>
            )}

            <LoyaltySection />
          </main>
        </div>
      </div>

      <Footer showCTA={false} />
    </div>
  );
};

export default Account;
